import i18next, {
  type BackendModule,
  type ReadCallback,
  type i18n,
} from 'i18next';
import { initReactI18next } from 'react-i18next';
import { api } from '@/shared/api/client';

export const DEFAULT_LANGUAGE = 'EN';
export const TRANSLATIONS_NAMESPACE = 'translation';

export type TranslationBundle = Record<string, Record<string, string>>;

export const fetchTranslations = (locale: string): Promise<TranslationBundle> =>
  api.get<TranslationBundle>(
    `config/translations/${encodeURIComponent(locale)}`,
  );

export const apiBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language: string, _namespace: string, callback: ReadCallback) {
    fetchTranslations(language)
      .then((bundle) => callback(null, bundle ?? {}))
      .catch((error: unknown) => callback(error as Error, null));
  },
};

export interface InitOptions {
  language?: string;
  /** Skip the HTTP backend and use inline resources (tests). */
  resources?: Record<string, { translation: TranslationBundle }>;
}

export const createI18n = (options: InitOptions = {}): i18n => {
  const instance = i18next.createInstance();
  instance.use(initReactI18next);
  if (!options.resources) instance.use(apiBackend);
  void instance.init({
    lng: options.language ?? DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    ns: [TRANSLATIONS_NAMESPACE],
    defaultNS: TRANSLATIONS_NAMESPACE,
    keySeparator: '.',
    nsSeparator: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    partialBundledLanguages: true,
    parseMissingKeyHandler: (key: string) => instance.hasLoadedNamespace(TRANSLATIONS_NAMESPACE) ? key : '',
    ...(options.resources ? { resources: options.resources } : {}),
    react: { useSuspense: false },
  });
  return instance;
};

/** Application-wide instance. Pages call `useTranslation()` from react-i18next. */
export const i18nInstance = createI18n();

/** Switches the language and reloads its translations. */
export const changeLanguage = async (
  language: string,
  instance: i18n = i18nInstance,
): Promise<void> => {
  await instance.reloadResources(language, TRANSLATIONS_NAMESPACE);
  await instance.changeLanguage(language);
};
